import { LanguageCode } from '@/types/language';
import { RemixBrowser } from '@remix-run/react';
import { init, replayIntegration } from '@sentry/remix';
import i18next from 'i18next';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { getEnv } from '@/lib/utils';

init({
  dsn: getEnv('VITE_SENTRY_DNS'),

  integrations: [
    replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    })
  ],

  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1
});

async function hydrate() {
  try {
    // Get the CMS API data from the global variable
    const cmsData = (window as any).__CMS_LANGUAGE_DATA__;

    // If language information is stored in localStorage, use it only if window exists
    let storedLanguage: string | null = null;
    if (typeof window !== 'undefined') {
      storedLanguage = localStorage.getItem('i18nextLng');
    }

    await i18next.use(initReactI18next).init({
      // Use the localStorage value first, if not available then use CMS data, otherwise default to 'en'
      lng: storedLanguage || cmsData?.language || LanguageCode.EN,
      fallbackLng: cmsData?.fallbackLng || LanguageCode.EN,
      supportedLngs: cmsData?.supportedLngs || [
        LanguageCode.EN,
        LanguageCode.AR
      ],
      ns: cmsData?.namespace ? [cmsData.namespace] : ['common'],
      defaultNS: cmsData?.defaultNS || 'common',
      // Use only CMS translation data - NO HttpApi backend
      resources:
        cmsData && cmsData.translations
          ? {
              [cmsData.language]: {
                [cmsData.defaultNS || 'common']: cmsData.translations
              }
            }
          : {
              [LanguageCode.EN]: {
                common: {}
              }
            },
      // Disable using Suspense
      react: { useSuspense: false },
      // Disable loading translations from files
      initImmediate: true
    });

    // Once i18next is initialized, proceed to ReactDOM.hydrate
    startTransition(() => {
      hydrateRoot(
        document,
        <I18nextProvider i18n={i18next}>
          <StrictMode>
            <RemixBrowser />
          </StrictMode>
        </I18nextProvider>
      );
    });
  } catch {
    // Fallback hydration without i18n
    startTransition(() => {
      hydrateRoot(
        document,
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      );
    });
  }
}

if (typeof window !== 'undefined') {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(hydrate);
  } else {
    // Safari doesn't support requestIdleCallback
    // https://caniuse.com/requestidlecallback
    window.setTimeout(hydrate, 1);
  }
}
